<template>
    <div v-if="props.imageElement" class="image-effects-properties-wrapper">
        <label>Map Styles</label>
        <div class="divider mb-3" />
        <div v-if="hasMapStyles" class="draggable-list-wrapper mb-3">
            <map-style-properties-list
                :items="model.mapConfig?.availableStyles"
                :map-config="model.mapConfig"
                :disabled="isDisabled"
                :default-style="model.mapConfig?.defaultStyle"
                @remove="removeMapStyle"
                @update="updateMapStyle"
                @reorder="handleReorder"
                @set-default="handleSetDefault"
            />
        </div>
        <div class="d-flex justify-content-center">
            <c-button
                class="w-full"
                size="md"
                text="Add Map Style"
                variant="light"
                :icon="['fa-solid', 'fa-sparkles']"
                :disabled="isDisabled"
                @click="openAddMapStyleModal"
            />
            <map-style-properties-create @create="createMapStyle" />
        </div>
        <div class="footer mt-4">
            <div class="divider mb-3" />
            <p>
                Choose one or multiple styles. Add custom or preset styles for customers to pick during personalization.
                Learn more in the
                <a
                    href="https://help.customily.com/hc/en-us/articles/16195841828891-Map"
                    rel="noopener noreferrer"
                    target="_blank"
                    >docs</a
                >.
            </p>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, watch, reactive, ref, provide, nextTick } from 'vue';
import CButton from '@/components/shared/CButton';
import type { CanvasEx } from '@/typings/canvas';
import type { ImagePlaceholderElement } from '@/typings/customilyElements/canvasElements';
import MapStylePropertiesList from '@/components/DesignStudio/MapStyleProperties/components/list/MapStylePropertiesList.vue';
import MapStylePropertiesCreate from '@/components/DesignStudio/MapStyleProperties/components/create/MapStylePropertiesCreate.vue';
import store from '@/store';
import { MAP_STYLE_PRESETS } from '@/components/DesignStudio/MapStyleProperties/presets';

const DEFAULT_MAP_CONFIG = {
    hasPin: false,
    latitude: 17.9,
    longitude: -37.34,
    zoom: 1.077,
    availableStyles: [MAP_STYLE_PRESETS[0].value],
    defaultStyle: MAP_STYLE_PRESETS[0].value
};

interface Props {
    canvas: CanvasEx;
    imageElement: ImagePlaceholderElement;
    editMode?: boolean;
}
interface Emits {
    (event: 'update-map', object: ImagePlaceholderElement): void;
}

const emit = defineEmits<Emits>();

const props = defineProps<Props>();

const model = reactive({
    mapConfig: props.imageElement?.mapConfig || { ...DEFAULT_MAP_CONFIG }
});

const openMapStyleId = ref<number | null>(null);
provide('openMapStyleId', openMapStyleId);

const isDisabled = computed(() => props.imageElement?.locked);
const hasMapStyles = computed(() => model.mapConfig?.availableStyles?.length > 0);

const updateMapStyles = () => {
    if (!props.imageElement || isDisabled.value) return;

    props.imageElement.set('mapConfig', { ...model.mapConfig });

    if (props.editMode) {
        props.imageElement.setEditedProp('mapConfig');
    }

    props.canvas.requestRenderAll();
    emit('update-map', props.imageElement);
};
const openAddMapStyleModal = () => {
    store?.dispatch('mapStyleProperties/openAddModal');
};

const removeMapStyle = (mapStyle: string) => {
    if (!model.mapConfig || isDisabled.value) return;

    const index = model.mapConfig?.availableStyles?.findIndex((style) => style === mapStyle);

    if (index !== -1) {
        const isDefaultStyle = isDefault(index);
        model.mapConfig.availableStyles.splice(index, 1);
        if (isDefaultStyle) {
            model.mapConfig.defaultStyle = model.mapConfig.availableStyles[0] || '';
        }
        updateMapStyles();
    }
};

const updateMapStyle = async (data: { id: number; value: string }) => {
    if (!model.mapConfig || isDisabled.value) return;
    const { id, value } = data;

    const exist = model.mapConfig.availableStyles[id];

    // Validate that the index exists and the provided value is not empty
    if (typeof exist === 'undefined' || exist === null) return;
    if (!value) return;

    const isDefaultStyle = isDefault(id);
    model.mapConfig.availableStyles[id] = value;
    model.mapConfig.availableStyles = [...new Set(model.mapConfig.availableStyles)];

    if (isDefaultStyle) {
        model.mapConfig.defaultStyle = value;
    }

    updateMapStyles();
};

const isDefault = (index: number) => {
    return model.mapConfig.availableStyles[index] === model.mapConfig.defaultStyle;
};

const isCustomStyle = (style: string) => {
    return style === 'username/style_id';
};

const createMapStyle = async (value: string) => {
    if (!model.mapConfig || isDisabled.value) return;

    if (!value) return;

    model.mapConfig.availableStyles.push(value);
    model.mapConfig.availableStyles = [...new Set(model.mapConfig.availableStyles)];

    updateMapStyles();
    await nextTick();
    const isCustom = isCustomStyle(value);
    if (isCustom) {
        openMapStyleId.value = model.mapConfig.availableStyles.length - 1;
    }
};

const handleReorder = (mapStyles: string[]) => {
    if (!model.mapConfig || isDisabled.value) return;

    model.mapConfig.availableStyles = mapStyles;
    updateMapStyles();
};

const handleSetDefault = (id: number) => {
    if (!model.mapConfig || isDisabled.value) return;

    model.mapConfig.defaultStyle = model.mapConfig.availableStyles[id];
    updateMapStyles();
};

watch(
    () => props.imageElement?.mapConfig,
    () => {
        if (props.imageElement?.mapConfig) {
            model.mapConfig = props.imageElement.mapConfig;
        } else {
            model.mapConfig = { ...DEFAULT_MAP_CONFIG };
        }
    },
    { deep: true }
);
</script>

<style scoped lang="scss">
@import 'styles/utilities/functions';
.draggable-list-wrapper {
    border: 1px solid var(--gray-light);
    border-radius: 0.25rem;
    overflow-y: auto;

    &::-webkit-scrollbar {
        width: 0.375rem;
        height: 0.375rem;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
        border: none;
        box-shadow: unset;
    }
    &::-webkit-scrollbar-thumb {
        background-color: #e6e6e6;
    }
}
.empty-state {
    border: 1px solid var(--gray-light);
    border-radius: 0.25rem;
}

.w-full {
    width: 100%;
}

.divider {
    border-top: 1px solid var(--gray-light);
}
</style>
